import { defineStore } from "pinia";
import { useApi } from "@/composables/useApi";
import type {
  ProfileData,
  AdditionalProfileData,
} from "@/modules/Profile/types/profile";

// Define the ProfileResponse interface based on the API response structure
interface ProfileResponse {
  avatar: string | null;
  phone: string;
  passport: string;
  pinfl: string;
  full_name: string;
  birth_date: string;
  gender: string;
  region: string;
  district: string;
  address: string;
  school_name: string;
  admission_type: string;
  study_language: string;
  study_form: string;
  study_speciality: string;
  created_at: string;
  student_group: string;
  study_year: string;
  kurs: string;
}

export const usePersonalInfoStore = defineStore("personalInfo", {
  state: () => ({
    loading: false,
    avatar: null as string | null,
    profileData: {
      number: "-",
      passport: "-",
      jshr: "-",
      fullName: "-",
      birthday: "-",
      gender: "-",
      region: "-",
      district: "-",
      address: "-",
      school: "-",
    } as ProfileData,
    additionalData: {
      examType: "-",
      language: "-",
      studyFormat: "-",
      studyDirection: "-",
      studentGroup: "-",
      studyYear: "-",
    } as AdditionalProfileData,
    dataFetched: false,
    created_at: null,
  }),
  actions: {
    async fetchProfileData() {
      // If data is already fetched, don't fetch again
      if (this.dataFetched) return;

      try {
        this.loading = true;
        const res = await useApi().$get<ProfileResponse>(
          "/v2/users/get-apply-info-profile/",
        );

        this.avatar = res.avatar;
        this.profileData = {
          number: res.phone || "-",
          passport: res.passport || "-",
          jshr: res.pinfl || "-",
          fullName: res.full_name || "-",
          birthday: res.birth_date || "-",
          gender: res.gender || "-",
          region: res.region || "-",
          district: res.district || "-",
          address: res.address || "-",
          school: res.school_name || "-",
        };
        this.additionalData = {
          examType: res.admission_type || "-",
          language: res.study_language || "-",
          // A slug (daytime / evening / external); the view translates it via $t().
          studyFormat: res.study_form || "-",
          studyDirection: res.study_speciality || "-",
          studentGroup: res.student_group || "-",
          // "Ta'lim bosqichi" is the course. study_year is the year the contract started, which
          // printed as "2026-kurs".
          studyYear: res.kurs || "-",
        };
        this.dataFetched = true;
        this.created_at = res?.created_at;
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        this.loading = false;
      }
    },
    resetStore() {
      this.loading = false;
      this.avatar = null;
      this.profileData = {
        number: "-",
        passport: "-",
        jshr: "-",
        fullName: "-",
        birthday: "-",
        gender: "-",
        region: "-",
        district: "-",
        address: "-",
        school: "-",
      };
      this.additionalData = {
        examType: "-",
        language: "-",
        studyFormat: "-",
        studyDirection: "-",
        studentGroup: "-",
        studyYear: "-",
      };
      this.dataFetched = false;
    },
  },
  getters: {
    isLoading: (state) => state.loading,
    getAvatar: (state) => state.avatar,
    getProfileData: (state) => state.profileData,
    getAdditionalData: (state) => state.additionalData,
    hasData: (state) => state.dataFetched,
  },
});
